import React, { useEffect, useMemo, useRef, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Button, Alert, Spinner, Badge } from '../components/ui';
import DashboardCard from '../components/DashboardCard';
import GroupAssignmentPicker from '../components/GroupAssignmentPicker';
import RichHtmlEditor from '../components/RichHtmlEditor';
import ContentCalendarView from '../components/ContentCalendarView';
import { findScheduleConflicts } from '../utils/contentCalendar';
import { BRAND_ASSET_INSERT_WIDTH } from '../constants/brandAssetTypes';
import useOrganizationBrandColors from '../hooks/useOrganizationBrandColors';
import useOverlayClose from '../hooks/useOverlayClose';
import { useT, pick } from '../i18n';
import './Campaigns.css';

const messages = {
  docTitle: { tr: 'Kampanyalar - Signature Manager', en: 'Campaigns - Signature Manager' },
  statusInactive: { tr: 'Pasif', en: 'Inactive' },
  statusScheduled: { tr: 'Planlandı', en: 'Scheduled' },
  statusExpired: { tr: 'Süresi Doldu', en: 'Expired' },
  statusActive: { tr: 'Aktif', en: 'Active' },
  noClicks: { tr: 'Son 30 günde tıklama yok.', en: 'No clicks in the last 30 days.' },
  needGroup: { tr: 'En az bir grup seçmelisiniz.', en: 'Select at least one group.' },
  saveFailed: { tr: 'Kaydedilemedi: {msg}', en: 'Couldn’t save: {msg}' },
  confirmDelete: { tr: 'Bu kampanyayı silmek istediğinize emin misiniz?', en: 'Are you sure you want to delete this campaign?' },
  deleteFailed: { tr: 'Silinemedi: {msg}', en: 'Couldn’t delete: {msg}' },
  analyticsFailed: { tr: 'Analitik alınamadı: {msg}', en: 'Couldn’t load analytics: {msg}' },
  title: { tr: 'Kampanyalar', en: 'Campaigns' },
  hint: {
    tr: 'Zamanlı ve tıklaması takip edilen pazarlama bannerları oluşturun, hangi gruplara gösterileceğini seçin ve imzanın üstünde mi altında mı yer alacağını belirleyin — imzaya otomatik olarak eklenir. Aynı anda birden fazla aktif kampanya aynı grubu hedefliyorsa en son oluşturulan gösterilir.',
    en: 'Create scheduled marketing banners with click tracking, choose which groups see them and whether they appear above or below the signature — they’re added to the signature automatically. If several active campaigns target the same group at once, the most recently created one is shown.',
  },
  newCampaign: { tr: '+ Yeni Kampanya', en: '+ New Campaign' },
  list: { tr: 'Liste', en: 'List' },
  calendar: { tr: 'Takvim', en: 'Calendar' },
  error: { tr: 'Hata: {msg}', en: 'Error: {msg}' },
  conflictWarning: {
    tr: 'Aynı grubu hedefleyen ve tarihleri çakışan kampanyalar var — takvimde turuncu işaretli. En son oluşturulan kazanır, diğerleri o tarih aralığında gösterilmez.',
    en: 'Some campaigns target the same group with overlapping dates — marked orange on the calendar. The most recently created one wins; the others aren’t shown in that date range.',
  },
  editTitle: { tr: 'Kampanyayı Düzenle', en: 'Edit Campaign' },
  createTitle: { tr: 'Yeni Kampanya', en: 'New Campaign' },
  content: { tr: 'İçerik', en: 'Content' },
  contentHint: {
    tr: 'Banner görselini editörün resim ekleme aracıyla ekleyin, isterseniz metin/link ile zenginleştirin.',
    en: 'Add the banner image with the editor’s image tool, and enrich it with text/links if you like.',
  },
  fromLibrary: { tr: 'Marka Kütüphanesinden Ekle', en: 'Insert from Brand Library' },
  targetUrl: { tr: 'Hedef URL', en: 'Target URL' },
  targetUrlPlaceholder: { tr: 'https://.../kampanya', en: 'https://.../campaign' },
  start: { tr: 'Başlangıç', en: 'Start' },
  end: { tr: 'Bitiş', en: 'End' },
  targetGroups: { tr: 'Hedef Gruplar', en: 'Target Groups' },
  targetGroupsHint: {
    tr: 'Bu kampanyanın gösterileceği grupları seçin. Hiçbir grup seçilmezse kampanya hiç kimseye gösterilmez.',
    en: 'Choose the groups that see this campaign. If no group is selected, nobody sees it.',
  },
  position: { tr: 'Konum', en: 'Position' },
  positionHint: { tr: 'Kampanya imzanın üstünde mi altında mı gösterilsin?', en: 'Show the campaign above or below the signature?' },
  top: { tr: 'Üstte', en: 'Above' },
  bottom: { tr: 'Altında', en: 'Below' },
  noAssets: { tr: 'Henüz marka varlığı yüklenmemiş.', en: 'No brand assets uploaded yet.' },
  openAssets: { tr: 'Marka Varlıkları\'nı aç', en: 'Open Brand Assets' },
  notFound: { tr: 'Aradığınızı bulamadınız mı?', en: 'Can’t find what you’re looking for?' },
  seeAll: { tr: 'Tümünü Gör', en: 'See All' },
  analyticsTitle: { tr: '{name} — Analitik', en: '{name} — Analytics' },
  impressions: { tr: 'Gösterim', en: 'Impressions' },
  clicks: { tr: 'Tıklama', en: 'Clicks' },
  dailyClicks: { tr: 'Son 30 Gün — Günlük Tıklama', en: 'Last 30 Days — Daily Clicks' },
  colGroups: { tr: 'Gruplar', en: 'Groups' },
  colRange: { tr: 'Tarih Aralığı', en: 'Date Range' },
  colAction: { tr: 'Aksiyon', en: 'Actions' },
  empty: { tr: 'Henüz kampanya oluşturulmadı.', en: 'No campaigns created yet.' },
  conflict: { tr: 'Çakışma', en: 'Conflict' },
  groupCount: { tr: '{n} grup', en: ({ n }) => `${n} group${n === 1 ? '' : 's'}` },
  analytics: { tr: 'Analitik', en: 'Analytics' },
};

const emptyForm = {
  name: '', html_content: '', target_url: '', start_date: '', end_date: '', is_active: true,
};

function toDatetimeLocal(value) {
  if (!value) return '';
  const d = new Date(value);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function campaignStatus(campaign) {
  const now = new Date();
  const start = new Date(campaign.start_date);
  const end = new Date(campaign.end_date);
  if (!campaign.is_active) return { label: pick(messages.statusInactive), variant: 'danger' };
  if (now < start) return { label: pick(messages.statusScheduled), variant: 'warning' };
  if (now > end) return { label: pick(messages.statusExpired), variant: 'danger' };
  return { label: pick(messages.statusActive), variant: 'success' };
}

function DailyClicksChart({ series }) {
  const { t } = useT(messages);
  if (!series || series.length === 0) {
    return <p className="campaign-analytics-empty">{t('noClicks')}</p>;
  }
  const max = Math.max(...series.map(d => d.clicks), 1);
  return (
    <div className="campaign-bar-chart">
      {series.map(d => (
        <div className="campaign-bar-col" key={d.date}>
          <div className="campaign-bar" style={{ height: `${(d.clicks / max) * 100}%` }} title={`${d.date}: ${d.clicks}`} />
          <span className="campaign-bar-label">{d.date.slice(5)}</span>
        </div>
      ))}
    </div>
  );
}

export default function Campaigns() {
  const overlayClose = useOverlayClose(() => closeForm());
  const analyticsOverlayClose = useOverlayClose(() => setAnalyticsFor(null));
  const { t, formatDate } = useT(messages);
  useEffect(() => {
    document.title = t('docTitle');
  }, [t]);

  const [brandColors] = useOrganizationBrandColors();

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [analyticsFor, setAnalyticsFor] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [groups, setGroups] = useState([]);
  const [assignments, setAssignments] = useState({});
  const [position, setPosition] = useState('bottom');
  const [viewMode, setViewMode] = useState('list');

  // Marka Kütüphanesinden Ekle — quick-insert picker for the campaign's
  // RichHtmlEditor, separate from TinyMCE's own generic "insert image"
  // dialog (which already lists brand assets too, see image_list in
  // emailEditorConfig.js) — this one is a purpose-labeled shortcut that
  // sits outside the editor chrome, easier to notice than a dropdown
  // buried inside a dialog.
  const editorRef = useRef(null);
  const [showAssetPicker, setShowAssetPicker] = useState(false);
  const [brandAssets, setBrandAssets] = useState([]);
  const [brandAssetsLoading, setBrandAssetsLoading] = useState(false);

  const calendarItems = useMemo(() => campaigns.map(c => ({
    id: c.id, name: c.name, groups: c.groups || [],
    start_date: c.start_date ? new Date(c.start_date) : null,
    end_date: c.end_date ? new Date(c.end_date) : null,
  })), [campaigns]);
  const conflictIds = useMemo(() => findScheduleConflicts(calendarItems), [calendarItems]);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(endpoints.signatures.campaigns.list);
      setCampaigns(data.results || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const data = await api.get(endpoints.signatures.groups.visibility);
      setGroups(data.filter(g => g.visible).map(g => ({ id: g.group_name, name: g.group_name })));
    } catch (err) {
      setGroups([]);
    }
  };

  useEffect(() => { load(); loadGroups(); }, []);

  useEffect(() => {
    if (!showForm) return;
    setBrandAssetsLoading(true);
    api.get(endpoints.signatures.brandAssets.list)
      .then(data => setBrandAssets(data || []))
      .catch(() => setBrandAssets([]))
      .finally(() => setBrandAssetsLoading(false));
  }, [showForm]);

  const insertBrandAsset = (asset) => {
    // Native width="" attribute, NOT style="max-width:100%" — see
    // BRAND_ASSET_INSERT_WIDTH's comment: a CSS percentage width breaks in
    // real Outlook sends (renders as a broken-image placeholder) even
    // though it looks fine in this browser-based editor preview.
    const width = BRAND_ASSET_INSERT_WIDTH[asset.asset_type] || BRAND_ASSET_INSERT_WIDTH.other;
    editorRef.current?.insertContent(`<img src="${asset.url}" alt="${asset.name}" width="${width}" />`);
    editorRef.current?.focus();
    setShowAssetPicker(false);
  };

  const toggleGroup = (id) => {
    setAssignments(prev => {
      const copy = { ...prev };
      if (copy[id] != null) delete copy[id];
      else copy[id] = 1;
      return copy;
    });
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setAssignments({});
    setPosition('bottom');
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      name: c.name, html_content: c.html_content, target_url: c.target_url,
      start_date: toDatetimeLocal(c.start_date), end_date: toDatetimeLocal(c.end_date),
      is_active: c.is_active,
    });
    setAssignments(Object.fromEntries((c.groups || []).map(g => [g, 1])));
    setPosition(c.position || 'bottom');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditing(null);
    setForm(emptyForm);
    setAssignments({});
    setPosition('bottom');
  };

  const handleFieldChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (Object.keys(assignments).length === 0) {
      setError(t('needGroup'));
      return;
    }
    setSaving(true);
    setError(null);
    const body = {
      ...form,
      start_date: new Date(form.start_date).toISOString(),
      end_date: new Date(form.end_date).toISOString(),
      position,
      group_names: Object.keys(assignments),
    };
    try {
      if (editing) {
        await api.put(endpoints.signatures.campaigns.detail(editing.id), body);
      } else {
        await api.post(endpoints.signatures.campaigns.create, body);
      }
      closeForm();
      await load();
    } catch (err) {
      const errMsg = err.response?.data
        ? Object.entries(err.response.data).map(([k, v]) => `${k}: ${v}`).join(' | ')
        : err.message;
      setError(t('saveFailed', { msg: errMsg }));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('confirmDelete'))) return;
    setError(null);
    try {
      await api.delete(endpoints.signatures.campaigns.detail(id));
      await load();
    } catch (err) {
      setError(t('deleteFailed', { msg: err.message }));
    }
  };

  const openAnalytics = async (campaign) => {
    setAnalyticsFor(campaign);
    setAnalyticsLoading(true);
    setAnalytics(null);
    try {
      const data = await api.get(endpoints.signatures.campaigns.analytics(campaign.id));
      setAnalytics(data);
    } catch (err) {
      setError(t('analyticsFailed', { msg: err.message }));
    } finally {
      setAnalyticsLoading(false);
    }
  };

  return (
    <div className="page-stack">
      <h1>{t('title')}</h1>
      <p className="campaigns-hint">{t('hint')}</p>
      <div className="campaigns-toolbar">
        <Button onClick={openCreate}>{t('newCampaign')}</Button>
        <div className="campaigns-view-toggle">
          <Button variant={viewMode === 'list' ? 'primary' : 'secondary'} onClick={() => setViewMode('list')}>{t('list')}</Button>
          <Button variant={viewMode === 'calendar' ? 'primary' : 'secondary'} onClick={() => setViewMode('calendar')}>{t('calendar')}</Button>
        </div>
      </div>

      {error && <Alert variant="error">{t('error', { msg: error })}</Alert>}
      {viewMode === 'calendar' && conflictIds.size > 0 && (
        <Alert variant="warning">
          {t('conflictWarning')}
        </Alert>
      )}

      {showForm && (
        <div className="modal-overlay" {...overlayClose}>
          <div className="modal-window campaign-modal">
            <form onSubmit={handleSubmit} className="campaign-form">
              <h2>{editing ? t('editTitle') : t('createTitle')}</h2>
              <div className="form-group">
                <label>{t('name')}</label>
                <input type="text" name="name" value={form.name} onChange={handleFieldChange} required />
              </div>
              <div className="form-group">
                <label>{t('content')}</label>
                <p className="form-group-hint">{t('contentHint')}</p>
                <Button
                  type="button" variant="secondary" className="campaign-brand-library-btn"
                  onClick={() => setShowAssetPicker(true)}
                >
                  {t('fromLibrary')}
                </Button>
                <RichHtmlEditor
                  key={editing ? `edit-${editing.id}` : 'new'}
                  onInit={(evt, editor) => { editorRef.current = editor; }}
                  initialValue={form.html_content}
                  onEditorChange={content => setForm(prev => ({ ...prev, html_content: content }))}
                  height={360}
                  brandColors={brandColors}
                />
              </div>
              <div className="form-group">
                <label>{t('targetUrl')}</label>
                <input type="url" name="target_url" value={form.target_url} onChange={handleFieldChange} required placeholder={t('targetUrlPlaceholder')} />
              </div>
              <div className="campaign-form-dates">
                <div className="form-group">
                  <label>{t('start')}</label>
                  <input type="datetime-local" name="start_date" value={form.start_date} onChange={handleFieldChange} required />
                </div>
                <div className="form-group">
                  <label>{t('end')}</label>
                  <input type="datetime-local" name="end_date" value={form.end_date} onChange={handleFieldChange} required />
                </div>
              </div>
              <div className="form-group">
                <label>{t('targetGroups')}</label>
                <p className="form-group-hint">{t('targetGroupsHint')}</p>
                <GroupAssignmentPicker groups={groups} assignments={assignments} onToggleGroup={toggleGroup} />
              </div>
              <div className="form-group">
                <label>{t('position')}</label>
                <p className="form-group-hint">{t('positionHint')}</p>
                <div className="position-toggle">
                  <Button
                    type="button" variant={position === 'top' ? 'primary' : 'secondary'}
                    onClick={() => setPosition('top')}
                  >
                    {t('top')}
                  </Button>
                  <Button
                    type="button" variant={position === 'bottom' ? 'primary' : 'secondary'}
                    onClick={() => setPosition('bottom')}
                  >
                    {t('bottom')}
                  </Button>
                </div>
              </div>
              <label className="campaign-active-check">
                <input type="checkbox" name="is_active" checked={form.is_active} onChange={handleFieldChange} />
                <span>{t('active')}</span>
              </label>
              <div className="form-actions">
                <Button type="submit" disabled={saving}>{saving ? t('saving') : t('save')}</Button>
                <Button type="button" variant="secondary" onClick={closeForm}>{t('cancel')}</Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showAssetPicker && (
        <div className="modal-overlay" onClick={() => setShowAssetPicker(false)}>
          <div className="modal-window campaign-modal" onClick={e => e.stopPropagation()}>
            <h2>{t('fromLibrary')}</h2>
            {brandAssetsLoading ? (
              <div className="page-loading"><Spinner /></div>
            ) : brandAssets.length === 0 ? (
              <p className="campaign-analytics-empty">
                {t('noAssets')}{' '}
                <a href="/brand-assets" target="_blank" rel="noreferrer">{t('openAssets')}</a>
              </p>
            ) : (
              <>
                <div className="campaign-asset-picker-grid">
                  {[...brandAssets]
                    .sort((a, b) => {
                      const rank = type => (type === 'banner' ? 0 : type === 'logo' ? 1 : 2);
                      return rank(a.asset_type) - rank(b.asset_type);
                    })
                    .map(asset => (
                      <button
                        type="button"
                        key={asset.id}
                        className="campaign-asset-picker-item"
                        onClick={() => insertBrandAsset(asset)}
                      >
                        <img src={asset.url} alt={asset.name} />
                        <span>{asset.name}</span>
                      </button>
                    ))}
                </div>
                <p className="form-group-hint">
                  {t('notFound')} <a href="/brand-assets" target="_blank" rel="noreferrer">{t('seeAll')}</a>
                </p>
              </>
            )}
            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => setShowAssetPicker(false)}>{t('close')}</Button>
            </div>
          </div>
        </div>
      )}

      {analyticsFor && (
        <div className="modal-overlay" {...analyticsOverlayClose}>
          <div className="modal-window campaign-modal">
            <h2>{t('analyticsTitle', { name: analyticsFor.name })}</h2>
            {analyticsLoading ? (
              <div className="page-loading"><Spinner /></div>
            ) : analytics ? (
              <>
                <div className="campaign-analytics-tiles">
                  <DashboardCard CardIcon="👁" title={t('impressions')} count={analytics.impressions} color="purple" />
                  <DashboardCard CardIcon="🖱" title={t('clicks')} count={analytics.total_clicks} color="green" />
                  <DashboardCard CardIcon="📈" title="CTR" count={`%${analytics.ctr}`} color="orange" />
                </div>
                <h3 className="campaign-analytics-subheading">{t('dailyClicks')}</h3>
                <DailyClicksChart series={analytics.daily_clicks} />
              </>
            ) : null}
            <div className="form-actions">
              <Button type="button" variant="secondary" onClick={() => setAnalyticsFor(null)}>{t('close')}</Button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : viewMode === 'calendar' ? (
        <ContentCalendarView
          items={calendarItems}
          conflictIds={conflictIds}
          onSelectItem={(item) => {
            const c = campaigns.find(x => x.id === item.id);
            if (c) openEdit(c);
          }}
        />
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('name')}</th>
              <th>{t('status')}</th>
              <th>{t('colGroups')}</th>
              <th>{t('position')}</th>
              <th>{t('colRange')}</th>
              <th>{t('impressions')}</th>
              <th>{t('colAction')}</th>
            </tr>
          </thead>
          <tbody>
            {campaigns.length === 0 && (
              <tr><td colSpan={7} className="data-table-empty">{t('empty')}</td></tr>
            )}
            {campaigns.map(c => {
              const st = campaignStatus(c);
              return (
                <tr key={c.id}>
                  <td>
                    {c.name}
                    {conflictIds.has(c.id) && (
                      <Badge variant="warning" className="campaign-conflict-badge">{t('conflict')}</Badge>
                    )}
                  </td>
                  <td><Badge variant={st.variant}>{st.label}</Badge></td>
                  <td>{t('groupCount', { n: (c.groups || []).length })}</td>
                  <td><Badge variant="info">{c.position === 'top' ? t('top') : t('bottom')}</Badge></td>
                  <td>
                    {formatDate(c.start_date)} – {formatDate(c.end_date)}
                  </td>
                  <td>{c.impression_count}</td>
                  <td className="campaign-actions">
                    <Button variant="secondary" onClick={() => openAnalytics(c)}>{t('analytics')}</Button>
                    <Button variant="secondary" onClick={() => openEdit(c)}>{t('edit')}</Button>
                    <Button variant="danger" onClick={() => handleDelete(c.id)}>{t('delete')}</Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
